import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Badge, Table, ProgressBar, Tabs, Tab, Alert, Spinner, Form, Modal, Row, Col, Pagination } from 'react-bootstrap';
import api from '../api/axios';

// Types for the monitoring data
interface CronJobStatus {
    isRunning: boolean;
    status: string;
    jobId?: string;
    startTime?: string;
    totalOrganizations?: number;
    processedOrganizations?: number;
    completedOrganizations?: number;
    failedOrganizations?: number;
    overallProgress?: number;
    estimatedTimeRemaining?: string;
    performance?: {
        averageOrgProcessingTime: number;
        throughputPerHour: number;
        fastestOrgTime: number;
        slowestOrgTime: number;
    };
    currentOrgId?: string;
    errors?: Array<{
        message: string;
        timestamp: string;
        organizationId?: string;
    }>;
    message?: string;
    lastRunTime?: string | null;
    nextScheduledRun?: string | null;
}

interface JobHistory {
    jobId: string;
    status: string;
    startTime: string;
    endTime?: string;
    duration?: number;
    totalOrganizations: number;
    completedOrganizations: number;
    failedOrganizations: number;
    overallProgress: number;
    errorCount: number;
}

interface OrgSyncReport {
    organizationId: string;
    organizationName: string;
    hubspotContactsFetched: number;
    hubspotCompaniesFetched: number;
    hubspotTotalRecords: number;
    hubspotErrors: number;
    blackbaudContactsUpdated: number;
    blackbaudCustomFieldsUpdated: number;
    blackbaudTotalUpdates: number;
    blackbaudErrors: number;
    totalContactsProcessed: number;
    newContactsCreated: number;
    existingContactsUpdated: number;
    customFieldStats: {
        subscriptionPreferences: number;
        emailLogs: number;
        websiteInteractions: number;
    };
    status: string;
    startTime: string;
    endTime?: string;
    errorDetails: string[];
    successRate: number;
    totalProcessed: number;
    syncDate: string;
    _id?: string;
}

interface ContactDetail {
    constituentId: string;
    createdAt?: string;
    updatedAt?: string;
    syncDate?: string;
    totalFields: number;
    customFields: {
        fieldName: string;
        fieldValue: string;
        category: string;
        dateAdded?: string;
        dateModified?: string;
        type?: string;
        comment?: string;
        date?: string;
    }[];
}

interface OrganizationDetail {
    organizationId: string;
    organizationName: string;
    totalContacts: number;
    syncedContacts: number;
    failedContacts: number;
    totalCustomFields: number;
    createdCustomFields: number;
    updatedCustomFields: number;
    lastSyncTime?: string;
    contacts: ContactDetail[];
}

interface DashboardSummary {
    currentStatus: {
        isRunning: boolean;
        status: string;
        progress?: number;
    };
    organizations: {
        total: number;
        enterprise: number;
        regular: number;
    };
    recentActivity: {
        totalJobs: number;
        successfulJobs: number;
        failedJobs: number;
        totalOrgsProcessed: number;
        totalErrors: number;
    };
    performance: {
        averageJobDuration: number;
        averageOrgProcessingTime: number;
        totalProcessingTime: number;
    };
}

const CronJobMonitoringDashboard: React.FC = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [currentStatus, setCurrentStatus] = useState<CronJobStatus | null>(null);
    const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary | null>(null);
    const [jobHistory, setJobHistory] = useState<JobHistory[]>([]);
    const [orgReports, setOrgReports] = useState<OrgSyncReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [triggering, setTriggering] = useState(false);
    const [showTriggerModal, setShowTriggerModal] = useState(false);
    const [triggerConfig, setTriggerConfig] = useState({
        organizationIds: [] as string[],
        syncType: 'full' as 'full' | 'incremental'
    });
    const [organizations, setOrganizations] = useState<Array<{ _id: string; name: string }>>([]);

    // New state for organization and contact details modals
    const [showOrgDetailsModal, setShowOrgDetailsModal] = useState(false);
    const [showContactDetailsModal, setShowContactDetailsModal] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState<OrganizationDetail | null>(null);
    const [selectedContact, setSelectedContact] = useState<ContactDetail | null>(null);
    const [orgContacts, setOrgContacts] = useState<ContactDetail[]>([]);
    const [contactsLoading, setContactsLoading] = useState(false);

    // New state for error details modal
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorDetails, setErrorDetails] = useState<string[]>([]);

    // Pagination state for organization reports
    const [orgReportsPage, setOrgReportsPage] = useState(1);
    const [orgReportsLimit] = useState(10);
    const [orgReportsTotal, setOrgReportsTotal] = useState(0);

    // Filter state for organization reports
    // dateFrom/dateTo are UTC ISO strings derived from the selected local date
    const [orgReportsFilters, setOrgReportsFilters] = useState({
        search: '',
        status: '',
        date: '',
        dateFrom: '',
        dateTo: '',
    });

    // Polling interval (10 seconds)
    const POLLING_INTERVAL = 10000;

    // Fetch current status
    const fetchCurrentStatus = useCallback(async () => {
        try {
            const response = await api.get('/cron/status');
            const data = (response.data as any)?.data;
            if (data) {
                setCurrentStatus(data);
            } else {
                console.warn('No data in current status response:', response.data);
                setCurrentStatus(null);
            }
        } catch (error) {
            console.error('Failed to fetch current status:', error);
            setCurrentStatus(null);
        }
    }, []);

    // Fetch dashboard summary - using stats endpoint which returns the structure we saw
    const fetchDashboardSummary = useCallback(async () => {
        try {
            const response = await api.get('/cron/dashboard');
            const data = (response.data as any)?.data;
            if (data) {
                setDashboardSummary(data);
            } else {
                console.warn('No data in dashboard response:', response.data);
                setDashboardSummary(null);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard summary:', error);
            setDashboardSummary(null);
        }
    }, []);

    // Fetch job history
    const fetchJobHistory = useCallback(async (page = 1, limit = 10) => {
        try {
            const response = await api.get(`/cron/jobs?page=${page}&limit=${limit}`);
            const jobs = (response.data as any)?.data?.jobs;
            setJobHistory(Array.isArray(jobs) ? jobs : []);
        } catch (error) {
            console.error('Failed to fetch job history:', error);
            setJobHistory([]);
        }
    }, []);

    // Fetch organization reports from sync reports
    const fetchOrgReports = useCallback(async (page = 1, limit = 20, filters = {}) => {
        try {
            const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
            Object.entries(filters).forEach(([key, value]) => {
                if (value) params.append(key, value as string);
            });

            const response = await api.get(`/sync/reports?${params}`);
            const reports = (response.data as any)?.data?.reports;
            setOrgReports(Array.isArray(reports) ? reports : []);
            setOrgReportsTotal((response.data as any)?.data?.pagination?.total || 0);
        } catch (error) {
            console.error('Failed to fetch organization reports:', error);
            setOrgReports([]);
            setOrgReportsTotal(0);
        }
    }, []);

    // Fetch organizations for trigger modal
    const fetchOrganizations = useCallback(async () => {
        try {
            const response = await api.get('/organisation');
            const orgs = (response.data as any)?.data;
            if (Array.isArray(orgs)) {
                setOrganizations(orgs.filter((org: any) => org?.isEnterprise));
            } else {
                setOrganizations([]);
            }
        } catch (error) {
            console.error('Failed to fetch organizations:', error);
            setOrganizations([]);
        }
    }, []);
    const fetchOrganizationDetails = useCallback(async (orgId: string) => {
        setContactsLoading(true);
        try {
            const response = await api.get(`/cron/reports/${orgId}/details`);
            const orgData = (response.data as any)?.data;
            if (orgData) {
                setSelectedOrganization(orgData);
                setOrgContacts(orgData.contacts || []);
                setShowOrgDetailsModal(true);
            }
        } catch (error) {
            console.error('Failed to fetch organization details:', error);
            alert('Failed to load organization details');
        } finally {
            setContactsLoading(false);
        }
    }, []);

    // Fetch contact details with custom fields
    const fetchContactDetails = useCallback(async (orgId: string, contactId: string) => {
        try {
            const response = await api.get(`/cron/organizations/${orgId}/contacts/${contactId}`);
            const contactData = (response.data as any)?.data;
            if (contactData) {
                setSelectedContact(contactData);
                setShowContactDetailsModal(true);
            }
        } catch (error) {
            console.error('Failed to fetch contact details:', error);
            alert('Failed to load contact details');
        }
    }, []);

    // Trigger manual sync
    const handleTriggerSync = async () => {
        try {
            const payload: any = { syncType: triggerConfig.syncType };
            if (triggerConfig.organizationIds.length > 0) {
                payload.organizationIds = triggerConfig.organizationIds;
            }

            await api.post('/cron/trigger', payload);
            setShowTriggerModal(false);
            await fetchCurrentStatus();
            await fetchDashboardSummary();
        } catch (error: any) {
            console.error('Failed to trigger sync:', error);
            alert(error.response?.data?.message || 'Failed to trigger sync');
        } finally {
            setTriggering(false);
        }
    };

    // Format duration
    const formatDuration = (ms: number): string => {
        if (ms <= 0) return 'N/A';
        const hours = Math.floor(ms / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((ms % (1000 * 60)) / 1000);
        return hours > 0 ? `${hours}h ${minutes}m` : minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    };

    // Format timestamp
    const formatTimestamp = (timestamp: string): string => {
        return new Date(timestamp).toLocaleString();
    };

    // Get status badge color
    const getStatusBadgeColor = (status: string): string => {
        switch (status.toLowerCase()) {
            case 'running': return 'primary';
            case 'completed': return 'success';
            case 'failed': return 'danger';
            case 'pending': return 'warning';
            default: return 'secondary';
        }
    };

    // Initial data fetch
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            await Promise.all([
                fetchCurrentStatus(),
                fetchDashboardSummary(),
                fetchJobHistory(),
                fetchOrgReports(orgReportsPage, orgReportsLimit),
                fetchOrganizations()
            ]);
            setLoading(false);
        };

        loadInitialData();
    }, [fetchCurrentStatus, fetchDashboardSummary, fetchJobHistory, fetchOrgReports, fetchOrganizations]);

    // Set up polling for real-time updates
    useEffect(() => {
        const interval = setInterval(async () => {
            await fetchCurrentStatus();
            if (activeTab === 'overview') {
                await fetchDashboardSummary();
            }
        }, POLLING_INTERVAL);

        return () => clearInterval(interval);
    }, [activeTab, fetchCurrentStatus, fetchDashboardSummary]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-2">Loading monitoring dashboard...</span>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <Row className="mb-4">
                <Col>
                    <h1 className="mb-0">Cron Job Monitoring Dashboard</h1>
                    <p className="text-muted">3-hour scheduled sync jobs - HubSpot contacts to Blackbaud custom fields</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="primary"
                        onClick={() => setShowTriggerModal(true)}
                        disabled={currentStatus?.isRunning || triggering}
                    >
                        {triggering ? 'Triggering...' : 'Trigger Manual Sync'}
                    </Button>
                </Col>
            </Row>

            {/* Current Status Card */}
            <Card className="mb-4">
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <h5>Current Status</h5>
                            <div className="d-flex align-items-center mb-2">
                                <Badge bg={currentStatus?.isRunning ? 'success' : 'secondary'} className="me-2">
                                    {currentStatus?.isRunning ? 'Running' : 'Idle'}
                                </Badge>
                                {currentStatus?.jobId && (
                                    <small className="text-muted">Job ID: {currentStatus.jobId}</small>
                                )}
                            </div>
                            {currentStatus?.isRunning && currentStatus?.overallProgress !== undefined && (
                                <ProgressBar 
                                    now={currentStatus.overallProgress} 
                                    label={`${currentStatus.overallProgress}%`}
                                    className="mb-2"
                                />
                            )}
                        </Col>
                        <Col md={6}>
                            {currentStatus?.isRunning && (
                                <>
                                    <small className="text-muted d-block">
                                        Processed: {currentStatus.processedOrganizations || 0} / {currentStatus.totalOrganizations || 0}
                                    </small>
                                    <small className="text-muted d-block">
                                        Completed: {currentStatus.completedOrganizations || 0} | Failed: {currentStatus.failedOrganizations || 0}
                                    </small>
                                    {currentStatus.estimatedTimeRemaining && (
                                        <small className="text-muted d-block">
                                            ETA: {currentStatus.estimatedTimeRemaining}
                                        </small>
                                    )}
                                </>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Main Content Tabs */}
            <Tabs activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)} className="mb-4">
                
                {/* Overview Tab */}
                <Tab.Pane eventKey="overview" title="Overview">
                    {dashboardSummary && (
                        <Row>
                            <Col md={4}>
                                <Card className="mb-4">
                                    <Card.Body>
                                        <h6>Organizations</h6>
                                        <div className="d-flex justify-content-between">
                                            <span>Total:</span>
                                            <strong>{dashboardSummary.organizations.total}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Enterprise:</span>
                                            <strong className="text-primary">{dashboardSummary.organizations.enterprise}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Regular:</span>
                                            <strong>{dashboardSummary.organizations.regular}</strong>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="mb-4">
                                    <Card.Body>
                                        <h6>Recent Activity (7 days)</h6>
                                        <div className="d-flex justify-content-between">
                                            <span>Total Jobs:</span>
                                            <strong>{dashboardSummary.recentActivity.totalJobs}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Successful:</span>
                                            <strong className="text-success">{dashboardSummary.recentActivity.successfulJobs}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Failed:</span>
                                            <strong className="text-danger">{dashboardSummary.recentActivity.failedJobs}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Organizations Processed:</span>
                                            <strong>{dashboardSummary.recentActivity.totalOrgsProcessed}</strong>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="mb-4">
                                    <Card.Body>
                                        <h6>Performance Metrics</h6>
                                        <div className="d-flex justify-content-between">
                                            <span>Avg Job Duration:</span>
                                            <strong>{formatDuration(dashboardSummary.performance.averageJobDuration)}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Avg Org Processing:</span>
                                            <strong>{formatDuration(dashboardSummary.performance.averageOrgProcessingTime)}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Total Processing Time:</span>
                                            <strong>{formatDuration(dashboardSummary.performance.totalProcessingTime)}</strong>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    
                    {/* Current Status Message */}
                    {currentStatus?.message && (
                        <Alert variant="info">
                            <Alert.Heading>Status</Alert.Heading>
                            <p className="mb-0">{currentStatus.message}</p>
                            {currentStatus.lastRunTime && (
                                <small className="text-muted">Last run: {formatTimestamp(currentStatus.lastRunTime)}</small>
                            )}
                        </Alert>
                    )}
                    
                    {/* Show Recent Errors from Dashboard Summary */}
                    {dashboardSummary?.recentActivity?.totalErrors && dashboardSummary.recentActivity.totalErrors > 0 && (
                        <Alert variant="warning">
                            <Alert.Heading>Recent Activity</Alert.Heading>
                            <p className="mb-0">
                                There have been {dashboardSummary.recentActivity.totalErrors} errors 
                                across {dashboardSummary.recentActivity.totalJobs} jobs in the last 7 days.
                            </p>
                        </Alert>
                    )}
                </Tab.Pane>

                {/* Job History Tab */}
                <Tab.Pane eventKey="history" title="Job History">
                    <Card>
                        <Card.Header>
                            <h6 className="mb-0">Recent Job Executions</h6>
                        </Card.Header>
                        <Card.Body>
                            <Table responsive striped>
                                <thead>
                                    <tr>
                                        <th>Job ID</th>
                                        <th>Status</th>
                                        <th>Start Time</th>
                                        <th>Duration</th>
                                        <th>Organizations</th>
                                        <th>Success Rate</th>
                                        <th>Errors</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {jobHistory && Array.isArray(jobHistory) ? jobHistory.map((job) => (
                                        <tr key={job.jobId}>
                                            <td><code>{job.jobId}</code></td>
                                            <td>
                                                <Badge bg={getStatusBadgeColor(job.status)}>
                                                    {job.status}
                                                </Badge>
                                            </td>
                                            <td>{formatTimestamp(job.startTime)}</td>
                                            <td>{job.duration ? formatDuration(job.duration) : 'N/A'}</td>
                                            <td>{job.completedOrganizations}/{job.totalOrganizations}</td>
                                            <td>
                                                {job.totalOrganizations > 0 
                                                    ? Math.round((job.completedOrganizations / job.totalOrganizations) * 100)
                                                    : 0}%
                                            </td>
                                            <td>
                                                {job.errorCount > 0 && (
                                                    <Badge bg="danger">{job.errorCount}</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={7} className="text-center">No job history available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab.Pane>

                {/* Organization Reports Tab */}
                <Tab.Pane eventKey="organizations" title="Organization Reports">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">Organization-wise Sync Status</h6>
                                {/* Filters */}
                                <div className="d-flex gap-2">
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by organization..."
                                        className="w-auto"
                                        value={orgReportsFilters.search}
                                        onChange={(e) => {
                                            const searchTerm = e.target.value;
                                            const newFilters = { ...orgReportsFilters, search: searchTerm };
                                            setOrgReportsFilters(newFilters);
                                            setOrgReportsPage(1);
                                            fetchOrgReports(1, orgReportsLimit, newFilters);
                                        }}
                                    />
                                    <Form.Select
                                        className="w-auto"
                                        value={orgReportsFilters.status}
                                        onChange={(e) => {
                                            const statusFilter = e.target.value;
                                            const newFilters = { ...orgReportsFilters, status: statusFilter };
                                            setOrgReportsFilters(newFilters);
                                            setOrgReportsPage(1);
                                            fetchOrgReports(1, orgReportsLimit, newFilters);
                                        }}
                                    >
                                        <option value="">All Status</option>
                                        <option value="completed">Completed</option>
                                        <option value="failed">Failed</option>
                                        <option value="partial">Partial</option>
                                        <option value="running">Running</option>
                                        <option value="quota_exceed">Quota_Exceed</option>
                                    </Form.Select>
                                    <Form.Control
                                        type="date"
                                        className="w-auto"
                                        value={orgReportsFilters.date}
                                        onChange={(e) => {
                                            const dateFilter = e.target.value;
                                            // Convert browser-local day boundaries to UTC ISO so the
                                            // backend (which stores UTC) matches what the user sees.
                                            const dateFrom = dateFilter ? new Date(dateFilter + 'T00:00:00').toISOString() : '';
                                            const dateTo = dateFilter ? new Date(dateFilter + 'T23:59:59.999').toISOString() : '';
                                            const newFilters = { ...orgReportsFilters, date: dateFilter, dateFrom, dateTo };
                                            setOrgReportsFilters(newFilters);
                                            fetchOrgReports(1, orgReportsLimit, newFilters);
                                            setOrgReportsPage(1);
                                        }}
                                    />
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Table responsive striped>
                                <thead>
                                    <tr>
                                        <th>Organization</th>
                                        <th>Status</th>
                                        <th>HubSpot Contacts</th>
                                        <th>HubSpot Companies</th>
                                        <th>Blackbaud Updates</th>
                                        <th>Error Details</th>
                                        <th>Sync Time</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orgReports && Array.isArray(orgReports) ? orgReports.map((org, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <Button
                                                    variant="link"
                                                    className="p-0 text-decoration-none"
                                                    onClick={() => fetchOrganizationDetails(org.organizationId)}
                                                    style={{ color: 'inherit' }}
                                                >
                                                    {org.organizationName}
                                                </Button>
                                            </td>
                                            <td>
                                                <Badge bg={getStatusBadgeColor(org.status)}>
                                                    {org.status}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <div>Fetched: {org.hubspotContactsFetched}</div>
                                                    <div className="text-danger">Errors: {org.hubspotErrors}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <div>Fetched: {org.hubspotCompaniesFetched}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <div><strong>Total Contacts:</strong> {org.hubspotContactsFetched}</div>
                                                    <div className="text-success"><strong>New:</strong> {org.newContactsCreated || 0}</div>
                                                    <div className="text-primary"><strong>Updated:</strong> {org.existingContactsUpdated || 0}</div>
                                                    <div className="text-danger"><strong>Errors:</strong> {org.blackbaudErrors}</div>
                                                    <div className="text-muted">
                                                        <strong>Skipped:</strong> {org.hubspotContactsFetched - org.totalContactsProcessed || 0}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {org.errorDetails && org.errorDetails.length > 0 && (
                                                    <div>
                                                        <Badge bg="danger" className="me-1">
                                                            {org.errorDetails.length} errors
                                                        </Badge>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            className="ms-1"
                                                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                                e.stopPropagation();
                                                                setErrorDetails(org.errorDetails);
                                                                setShowErrorModal(true);
                                                            }}
                                                        >
                                                            View Errors
                                                        </Button>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="small">
                                                    {org.successRate}%
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    {formatTimestamp(org.startTime)}
                                                    {org.endTime && (
                                                        <div>→ {formatTimestamp(org.endTime)}</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => fetchOrganizationDetails(org?._id || '')}
                                                >
                                                    Details
                                                </Button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={8} className="text-center">No organization reports available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                            {/* Pagination */}
                            {orgReportsTotal > orgReportsLimit && (
                                <div className="d-flex justify-content-center mt-3">
                                    <Pagination>
                                        <Pagination.Prev
                                            onClick={() => {
                                                const newPage = Math.max(1, orgReportsPage - 1);
                                                setOrgReportsPage(newPage);
                                                fetchOrgReports(newPage, orgReportsLimit, orgReportsFilters);
                                            }}
                                            disabled={orgReportsPage === 1}
                                        />
                                        {Array.from({ length: Math.min(5, Math.ceil(orgReportsTotal / orgReportsLimit)) }, (_, i) => {
                                            const pageNum = Math.max(1, orgReportsPage - 2) + i;
                                            if (pageNum > Math.ceil(orgReportsTotal / orgReportsLimit)) return null;

                                            return (
                                                <Pagination.Item
                                                    key={pageNum}
                                                    active={pageNum === orgReportsPage}
                                                    onClick={() => {
                                                        setOrgReportsPage(pageNum);
                                                        fetchOrgReports(pageNum, orgReportsLimit, orgReportsFilters);
                                                    }}
                                                >
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}
                                        <Pagination.Next
                                            onClick={() => {
                                                const newPage = Math.min(Math.ceil(orgReportsTotal / orgReportsLimit), orgReportsPage + 1);
                                                setOrgReportsPage(newPage);
                                                fetchOrgReports(newPage, orgReportsLimit, orgReportsFilters);
                                            }}
                                            disabled={orgReportsPage === Math.ceil(orgReportsTotal / orgReportsLimit)}
                                        />
                                    </Pagination>
                                    <div className="ms-3 align-self-center">
                                        <small className="text-muted">
                                            Showing {((orgReportsPage - 1) * orgReportsLimit) + 1} to {Math.min(orgReportsPage * orgReportsLimit, orgReportsTotal)} of {orgReportsTotal} results
                                        </small>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Tab.Pane>
            </Tabs>

            {/* Manual Trigger Modal */}
            <Modal show={showTriggerModal} onHide={() => setShowTriggerModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Trigger Manual Sync</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Sync Type</Form.Label>
                            <Form.Select
                                value={triggerConfig.syncType}
                                onChange={(e) => setTriggerConfig(prev => ({
                                    ...prev,
                                    syncType: e.target.value as 'full' | 'incremental'
                                }))}
                            >
                                <option value="full">Full Sync</option>
                                <option value="incremental">Incremental Sync</option>
                            </Form.Select>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Organizations (optional - leave empty to sync all enterprise orgs)</Form.Label>
                            <Form.Select
                                multiple
                                value={triggerConfig.organizationIds}
                                onChange={(e) => {
                                    const values = Array.from(e.target.selectedOptions, option => option.value);
                                    setTriggerConfig(prev => ({ ...prev, organizationIds: values }));
                                }}
                            >
                                {organizations && Array.isArray(organizations) ? organizations.map(org => (
                                    <option key={org._id} value={org._id}>
                                        {org.name}
                                    </option>
                                )) : (
                                    <option disabled>Loading organizations...</option>
                                )}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Hold Ctrl/Cmd to select multiple organizations
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTriggerModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleTriggerSync} disabled={triggering}>
                        {triggering ? 'Triggering...' : 'Trigger Sync'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Organization Details Modal */}
            <Modal show={showOrgDetailsModal} onHide={() => setShowOrgDetailsModal(false)} dialogClassName="modal-90w">
                <Modal.Header closeButton>
                    <Modal.Title>Organization Details: {selectedOrganization?.organizationName}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {contactsLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                            <p className="mt-2">Loading contact details...</p>
                        </div>
                    ) : selectedOrganization ? (
                        <div>
                            {/* Organization Summary */}
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Card>
                                        <Card.Body>
                                            <h6>Sync Summary</h6>
                                            <div className="d-flex justify-content-between">
                                                <span>Total Contacts:</span>
                                                <strong>{selectedOrganization.totalContacts}</strong>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span>Synced:</span>
                                                <strong className="text-success">{selectedOrganization.syncedContacts}</strong>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span>Failed:</span>
                                                <strong className="text-danger">{selectedOrganization.failedContacts}</strong>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span>Success Rate:</span>
                                                <strong>
                                                    {selectedOrganization.totalContacts > 0
                                                        ? Math.round((selectedOrganization.syncedContacts / selectedOrganization.totalContacts) * 100)
                                                        : 0}%
                                                </strong>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body>
                                            <h6>Custom Fields</h6>
                                            <div className="d-flex justify-content-between">
                                                <span>Total Fields:</span>
                                                <strong>{selectedOrganization.totalCustomFields}</strong>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span>Created:</span>
                                                <strong className="text-success">{selectedOrganization.createdCustomFields}</strong>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span>Updated:</span>
                                                <strong className="text-primary">{selectedOrganization.updatedCustomFields}</strong>
                                            </div>
                                            {selectedOrganization.lastSyncTime && (
                                                <div className="d-flex justify-content-between">
                                                    <span>Last Sync:</span>
                                                    <small className="text-muted">{formatTimestamp(selectedOrganization.lastSyncTime)}</small>
                                                </div>
                                            )}
                                            <hr />
                                            <div className="d-flex justify-content-between">
                                                <span>
                                                    Skipped Contacts
                                                    <i 
                                                        className="fas fa-info-circle ms-1 text-muted" 
                                                        style={{ cursor: 'pointer', fontSize: '0.8em' }}
                                                        title="Contacts without rs_constituent_id property are skipped. These contacts exist in HubSpot but lack the required mapping to Blackbaud constituents."
                                                    ></i>
                                                </span>
                                                <strong className="text-warning">{selectedOrganization.totalContacts - selectedOrganization.syncedContacts || 0}</strong>
                                            </div>
                                            <div className="text-muted mt-2" style={{ fontSize: '0.8em' }}>
                                                <i className="fas fa-exclamation-triangle me-1"></i>
                                                Note: Contacts without rs_constituent_id property are skipped during sync
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Contacts Table */}
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">Contacts ({orgContacts.length})</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Table responsive striped>
                                        <thead>
                                            <tr>
                                                <th>System Record ID</th>
                                                <th>Created</th>
                                                <th>Updated</th>
                                                <th>Total Fields</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orgContacts.map((contact) => (
                                                <tr key={contact.constituentId}>
                                                    <td>{contact.constituentId}</td>
                                                    <td>{contact.createdAt ? formatTimestamp(contact.createdAt) : 'N/A'}</td>
                                                    <td>{contact.updatedAt ? formatTimestamp(contact.updatedAt) : 'N/A'}</td>
                                                    <td>
                                                        <Badge bg="info">{contact.totalFields}</Badge>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            onClick={() => fetchContactDetails(selectedOrganization?.organizationId || '', contact.constituentId)}
                                                        >
                                                            View Fields
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowOrgDetailsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Contact Details Modal */}
            <Modal show={showContactDetailsModal} onHide={() => setShowContactDetailsModal(false)} dialogClassName="modal-80w">
                <Modal.Header closeButton>
                    <Modal.Title>Contact Details: {selectedContact?.constituentId}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedContact ? (
                        <div>
                            {/* Contact Info */}
                            <Card className="mb-4">
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <strong>Contact Information</strong>
                                            <div className="mt-2">
                                                <div><strong>System Record ID:</strong> {selectedContact.constituentId}</div>
                                                <div><strong>Created:</strong> {selectedContact.createdAt ? formatTimestamp(selectedContact.createdAt) : 'N/A'}</div>
                                                <div><strong>Updated:</strong> {selectedContact.updatedAt ? formatTimestamp(selectedContact.updatedAt) : 'N/A'}</div>
                                                {selectedContact.syncDate && (
                                                    <div><strong>Sync Date:</strong> {formatTimestamp(selectedContact.syncDate)}</div>
                                                )}
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <strong>Sync Summary</strong>
                                            <div className="mt-2">
                                                <div><strong>Total Fields:</strong> {selectedContact.totalFields}</div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Custom Fields - Table View */}
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">Custom Fields ({selectedContact.customFields.filter(field => field.category !== 'General').length})</h6>
                                </Card.Header>
                                <Card.Body>
                                    {selectedContact.customFields.filter(field => field.category !== 'General').length > 0 ? (
                                        <Table responsive striped>
                                            <thead>
                                                <tr>
                                                    <th>Value</th>
                                                    <th>Category</th>
                                                    <th>Type</th>
                                                    <th>Comment</th>
                                                    <th>Date</th>
                                                    <th>Date Added</th>
                                                    <th>Date Modified</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedContact.customFields
                                                    .filter(field => field.category !== 'General')
                                                    .map((field, idx) => (
                                                        <tr key={idx}>
                                                            <td><strong className="text-primary">{field.fieldValue}</strong></td>
                                                            <td><Badge bg="secondary">{field.category}</Badge></td>
                                                            <td>{field.type || 'N/A'}</td>
                                                            <td>{field.comment || 'N/A'}</td>
                                                            <td>{field.date ? formatTimestamp(field.date) : 'N/A'}</td>
                                                            <td>{field.dateAdded ? formatTimestamp(field.dateAdded) : 'N/A'}</td>
                                                            <td>{field.dateModified ? formatTimestamp(field.dateModified) : 'N/A'}</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <div className="text-center py-3 text-muted">
                                            No custom fields available (excluding General category)
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowContactDetailsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Error Details Modal */}
            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Error Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {errorDetails.length > 0 ? (
                        <div>
                            <Alert variant="danger">
                                <strong>Total Errors: {errorDetails.length}</strong>
                            </Alert>
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {errorDetails.map((error, index) => (
                                    <Card key={index} className="mb-2">
                                        <Card.Body className="py-2">
                                            <small className="text-muted">Error #{index + 1}:</small>
                                            <div className="mt-1">
                                                {error}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <p className="text-muted">No error details available</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
                        Close
                    </Button>
                    {errorDetails.length > 0 && (
                        <Button
                            variant="outline-primary"
                            onClick={() => {
                                const errorText = errorDetails.join('\n\n');
                                navigator.clipboard.writeText(errorText);
                                alert('Error details copied to clipboard!');
                            }}
                        >
                            Copy All Errors
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CronJobMonitoringDashboard;